import { useEffect, useRef, useState } from 'react';
import { CompoundInterestParams, Currency } from '../../../../utils/compound-interest/types';
import { DEFAULT_PARAMS, DEFAULT_PARAMS_BY_CURRENCY } from '../../../../utils/compound-interest/constants';

interface PrefillPortfolioData {
    id: string;
    name: string;
    value: number;
    currency: string;
}

interface UseCompoundInterestPrefillParams {
    params: CompoundInterestParams;
    isLoggedIn: boolean;
    hasPortfolio: boolean;
    portfolio: PrefillPortfolioData | null;
    onPrefill: (params: CompoundInterestParams) => void;
}

const areParamsEqualForPortfolioReset = (a: CompoundInterestParams, b: CompoundInterestParams): boolean => {
    return (
        a.initialDeposit === b.initialDeposit &&
        a.regularDeposit === b.regularDeposit &&
        a.depositFrequency === b.depositFrequency &&
        a.duration === b.duration &&
        a.compoundingFrequency === b.compoundingFrequency &&
        a.inflationRate === b.inflationRate &&
        a.taxRate === b.taxRate &&
        a.currency === b.currency
    );
};

export const useCompoundInterestPrefill = ({
    params,
    isLoggedIn,
    hasPortfolio,
    portfolio,
    onPrefill,
}: UseCompoundInterestPrefillParams) => {
    const [hasUserEditedParams, setHasUserEditedParams] = useState(false);
    const [prefilledPortfolioName, setPrefilledPortfolioName] = useState('');
    const [hasPrefilledFromPortfolio, setHasPrefilledFromPortfolio] = useState(false);
    const lastPrefilledPortfolioId = useRef('');
    const lastPrefilledParams = useRef<CompoundInterestParams | null>(null);

    const selectedPortfolioId = portfolio?.id ?? '';
    const selectedPortfolioName = portfolio?.name ?? '';
    const portfolioValue = portfolio?.value ?? 0;
    const portfolioCurrency = portfolio?.currency ?? '';

    useEffect(() => {
        if (!isLoggedIn || !hasPortfolio || !selectedPortfolioId) {
            setPrefilledPortfolioName('');
            setHasPrefilledFromPortfolio(false);
            lastPrefilledParams.current = null;
            return;
        }

        const selectedPortfolioChanged = lastPrefilledPortfolioId.current !== selectedPortfolioId;

        if (!selectedPortfolioChanged) {
            return;
        }

        if (hasUserEditedParams) {
            setHasUserEditedParams(false);
        }

        const currency = (portfolioCurrency as Currency) || DEFAULT_PARAMS.currency;
        const currencyDefaults = DEFAULT_PARAMS_BY_CURRENCY[currency] ?? DEFAULT_PARAMS;

        const prefilledParams = {
            ...currencyDefaults,
            initialDeposit: Math.round(portfolioValue),
            interestRate: currencyDefaults.interestRate,
        };

        onPrefill(prefilledParams);

        setPrefilledPortfolioName(selectedPortfolioName);
        setHasPrefilledFromPortfolio(true);
        lastPrefilledParams.current = prefilledParams;
        lastPrefilledPortfolioId.current = selectedPortfolioId;
    }, [
        hasPortfolio,
        hasUserEditedParams,
        isLoggedIn,
        onPrefill,
        portfolioCurrency,
        portfolioValue,
        selectedPortfolioId,
        selectedPortfolioName,
    ]);

    const markParamsAsEdited = () => {
        setHasUserEditedParams(true);
    };

    const resetToPrefilledPortfolioParams = () => {
        if (!lastPrefilledParams.current) {
            return;
        }

        onPrefill(lastPrefilledParams.current);
        setHasUserEditedParams(false);
    };

    const canResetToPrefilledPortfolioParams =
        hasPrefilledFromPortfolio &&
        !!lastPrefilledParams.current &&
        !areParamsEqualForPortfolioReset(params, lastPrefilledParams.current);

    return {
        prefilledPortfolioName,
        hasPrefilledFromPortfolio,
        canResetToPrefilledPortfolioParams,
        markParamsAsEdited,
        resetToPrefilledPortfolioParams,
    };
};
